// Действията по плащането и по стоковата, общи за документа „Доставка" и
// началния екран: Отбележи като платена / Промени (модал „Плащане" с
// досегашното плащане) · Отбележи като неплатена (потвърждение) · Извади от
// фактурата (потвърждение) · Приключи без фактура (модал) · Върни за
// фактуриране. `onChanged(id)` — документът да се презареди.
import { useState } from "react";
import { App } from "antd";
import type { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import type { Delivery, DeliveryPaymentMethod } from "../../../../api/inventory";
import type { CloseWithoutInvoicePayload } from "../../../../api/inventoryDeliveries";
import {
  useCloseNotesWithoutInvoice,
  useDetachNotesFromInvoice,
  usePayDeliveries,
  useReopenNotesForInvoice,
  useUnpayDeliveries,
} from "../../../../hooks/useInventoryDeliveries";
import { useConfirm } from "../../components/confirm/useConfirm";
import { formatMoney } from "../../components/numbers";
import { useActionToast } from "../../components/useActionToast";
import { PayDeliveriesModal } from "../../payments/components/PayDeliveriesModal";
import { CloseWithoutInvoiceModal } from "./CloseWithoutInvoiceModal";
import { deliveryGrossOf, documentLabelOf } from "./deliveryMeta";
import { paymentInitialOf } from "./deliveryPaymentState";

type PaymentInput = { paidAt: Dayjs; method: DeliveryPaymentMethod; reference: string | null };

const errorDetail = (error: unknown) =>
  (error as { response?: { data?: { detail?: string } } }).response?.data?.detail;

export const useDeliveryDocumentActions = (onChanged: (id: number) => void) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const confirm = useConfirm();
  const showToast = useActionToast();
  const payMutation = usePayDeliveries();
  const unpayMutation = useUnpayDeliveries();
  const detachMutation = useDetachNotesFromInvoice();
  const closeMutation = useCloseNotesWithoutInvoice();
  const reopenMutation = useReopenNotesForInvoice();
  const [payTarget, setPayTarget] = useState<Delivery | null>(null);
  const [closeTargets, setCloseTargets] = useState<Delivery[]>([]);

  const label = (delivery: Delivery) => documentLabelOf(delivery, t);
  const failed = (fallback: string) => (error: unknown) => message.error(errorDetail(error) || t(fallback));

  // „Отбележи като платена" и „Промени" — повторното плащане презаписва (BE)
  const startPay = (delivery: Delivery) => setPayTarget(delivery);

  const confirmPay = (payment: PaymentInput) => {
    if (!payTarget) return;
    const target = payTarget;
    payMutation.mutate(
      {
        ids: [target.id],
        paidAt: payment.paidAt.toISOString(),
        method: payment.method,
        reference: payment.reference,
      },
      {
        onSuccess: () => {
          showToast({ text: t("inventoryPage.payDoneToast", { count: 1 }) });
          setPayTarget(null);
          onChanged(target.id);
        },
        onError: failed("inventoryPage.payError"),
      },
    );
  };

  const markUnpaid = async (delivery: Delivery) => {
    const noteCount = delivery.linkedNoteIds?.length ?? 0;
    const doc = label(delivery);
    const confirmed = await confirm({
      intent: "warning",
      title: t("inventoryPage.payUnpayTitle", { doc }),
      explanation: noteCount
        ? t("inventoryPage.payUnpayTextNotes", { doc, count: noteCount })
        : t("inventoryPage.payUnpayText", { doc }),
      confirmLabel: t("inventoryPage.payMarkUnpaid"),
      action: () => unpayMutation.mutateAsync([delivery.id]).catch((error) => {
        failed("inventoryPage.payUnpayError")(error);
        throw error;
      }),
    });
    if (!confirmed) return;
    showToast({ text: t("inventoryPage.payUnpaidToast", { doc }) });
    onChanged(delivery.id);
  };

  const detach = async (note: Delivery) => {
    const doc = label(note);
    const confirmed = await confirm({
      intent: "warning",
      title: t("inventoryPage.noteDetachTitle"),
      explanation: t("inventoryPage.noteDetachText", { doc }),
      confirmLabel: t("inventoryPage.noteDetachConfirm"),
      action: () => detachMutation.mutateAsync([note.id]).catch((error) => {
        failed("inventoryPage.noteDetachError")(error);
        throw error;
      }),
    });
    if (!confirmed) return;
    showToast({ text: t("inventoryPage.noteDetachedToast", { doc }) });
    onChanged(note.id);
  };

  const startClose = (notes: Delivery[]) => setCloseTargets(notes);

  const confirmClose = (payload: CloseWithoutInvoicePayload) =>
    closeMutation.mutate(payload, {
      onSuccess: (count) => {
        showToast({ text: t("inventoryPage.noteClosedToast", { count }) });
        if (closeTargets.length === 1) onChanged(closeTargets[0].id);
        setCloseTargets([]);
      },
      onError: failed("inventoryPage.noteCloseError"),
    });

  const reopen = (note: Delivery) =>
    reopenMutation.mutate([note.id], {
      onSuccess: () => {
        showToast({ text: t("inventoryPage.noteReopenedToast", { doc: label(note) }) });
        onChanged(note.id);
      },
      onError: failed("inventoryPage.noteReopenError"),
    });

  const modals = (
    <>
      <PayDeliveriesModal
        open={!!payTarget}
        subtitle={
          payTarget ? `${label(payTarget)} · ${formatMoney(deliveryGrossOf(payTarget))}` : ""
        }
        initial={payTarget ? paymentInitialOf(payTarget) : undefined}
        saving={payMutation.isPending}
        onCancel={() => setPayTarget(null)}
        onConfirm={confirmPay}
      />
      <CloseWithoutInvoiceModal
        open={closeTargets.length > 0}
        notes={closeTargets}
        saving={closeMutation.isPending}
        onCancel={() => setCloseTargets([])}
        onConfirm={confirmClose}
        onPickNone={() => message.info(t("inventoryPage.noteClosePick"))}
      />
    </>
  );

  return { startPay, markUnpaid, detach, startClose, reopen, modals };
};

export default useDeliveryDocumentActions;
